import { Box, Grid, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import CAppPaper from '../../../../app/components/CAppPaper';
import CAppSubTitle from '../../../../app/components/CAppSubTitle';
import CSynthesisIndicators from '../../features/fund-type/components/synthesis/venture_capital/CSynthesisIndicators';
import IInvestExpertiseFiltersResult from '../../interfaces/expertise/IInvestExpertiseFiltersResult';
import AppLoaderGraph from '../../../../app/features/loader/graph/AppLoaderGraph';
import CSynthesisGraphCapital from '../../features/fund-type/components/synthesis/venture_capital/CSynthesisGraphCapital';
import CSynthesisGraphTotalValue from '../../features/fund-type/components/synthesis/venture_capital/CSynthesisGraphTotalValue';
import IInvestKpis from '../../interfaces/IInvestKpis';
import moment from 'moment';
import investExpertiseSynthesisVentureCapitalLang from '../../features/fund-type/langs/investExpertiseSynthesisVentureCapitalLang';
import IInvestKpisByFund from '../../interfaces/IInvestKpisByFund';
import { TInvestFundCurrency } from '../../types/fund/TInvestFundCurrency';
import useInvestFunds from '../../hooks/fund/useInvestFunds';

type VInvestExpertiseSynthesisVentureCapitalProps = {
	resultData: IInvestExpertiseFiltersResult;
};

const VInvestExpertiseSynthesisVentureCapital = (props: VInvestExpertiseSynthesisVentureCapitalProps) => {
	const { resultData } = props;
	const { t } = useTranslation();

	const { funds: allFunds } = useInvestFunds();

	const [kpisByFunds, setKpisByFunds] = useState<IInvestKpisByFund[]>([]);
	const [currency, setCurrency] = useState<TInvestFundCurrency | null>(null);

	useEffect(() => {
		if (resultData && allFunds) {
			if (resultData.kpisByFund) {
				setKpisByFunds(resultData.kpisByFund);
				const currencies = _.uniqBy(
					allFunds.filter((x) => resultData.kpisByFund.map((f) => f.fund.id).includes(x.id)),
					'currency',
				).map((x) => x.currency);
				setCurrency(currencies && currencies.length === 1 ? currencies[0] : null);
			}
		}
		return () => {
			setKpisByFunds([]);
		};
	}, [resultData, allFunds]);

	return (
		<>
			{kpisByFunds === undefined ? (
				<Box sx={{ margin: '30px' }}>
					<AppLoaderGraph />
				</Box>
			) : kpisByFunds && kpisByFunds.length > 0 ? (
				<>
					{kpisByFunds.map((kpisByFund, index) => (
						<CSynthesisBlockVentureCapital
							position={index}
							key={index}
							creationDate={kpisByFund.fund.creationDate ?? undefined}
							isCoinvestment={kpisByFund.fund.isCoinvestment ?? undefined}
							title={kpisByFund.fund.name}
							currency={
								allFunds?.find((x) => x.id === kpisByFund.fund.id)?.currency as TInvestFundCurrency
							}
							kpis={{
								calledCapital: kpisByFund.kpis.calledCapital,
								committedCapital: kpisByFund.kpis.committedCapital,
								irr: kpisByFund.kpis.irr,
								irrIsNs: kpisByFund.kpis.irrIsNs,
								irrGross: kpisByFund.kpis.irrGross,
								irrGrossIsN: kpisByFund.kpis.irrGrossIsN,
								fees: kpisByFund.kpis.fees,
								tvpi: kpisByFund.kpis.tvpi,
								dpi: kpisByFund.kpis.dpi,
								rvpi: kpisByFund.kpis.rvpi,
								totalDistributed: kpisByFund.kpis.totalDistributed,
								distributionsRecallable: kpisByFund.kpis.distributionsRecallable,
								netAssetValue: kpisByFund.kpis.netAssetValue,
								totalValue: kpisByFund.kpis.totalValue,
							}}
						/>
					))}
					<CSynthesisBlockVentureCapital
						title={t(investExpertiseSynthesisVentureCapitalLang.title_total_of_positions.tag)}
						currency={currency}
						kpis={{
							...resultData.kpis,
							totalDistributed: _.sumBy(kpisByFunds, 'kpis.totalDistributed'),
							distributionsRecallable: _.sumBy(kpisByFunds, 'kpis.distributionsRecallable'),
							netAssetValue: _.sumBy(kpisByFunds, 'kpis.netAssetValue'),
							totalValue: _.sumBy(kpisByFunds, 'kpis.totalValue'),
						}}
						color="primary"
						variant="elevation"
						position={kpisByFunds.length}
					/>
				</>
			) : (
				''
			)}
		</>
	);
};

type CSynthesisBlockVentureCapitalProps = {
	creationDate?: Date;
	title: string;
	isCoinvestment?: boolean;
	kpis: IInvestKpis;
	color?: string;
	variant?: 'elevation' | 'outlined';
	position: number;
	currency: TInvestFundCurrency;
};

const CSynthesisBlockVentureCapital = (props: CSynthesisBlockVentureCapitalProps) => {
	const { position, isCoinvestment, title, variant, color, creationDate, kpis, currency } = props;

	const theme = useTheme();
	const { t } = useTranslation();

	return (
		<CAppPaper
			color={color ?? ''}
			variant="outlined"
			sx={{ marginBottom: '40px' }}
			className="expertise-content-synthesis-fund-stats"
			id={'expertise-content-synthesis-fund-stats-' + position}
		>
			<CAppSubTitle
				className="fund-stats-title"
				title={`${
					isCoinvestment === false
						? `${t(investExpertiseSynthesisVentureCapitalLang.title_fund.tag)} : `
						: isCoinvestment === true
						? `${t(investExpertiseSynthesisVentureCapitalLang.title_coinvestment.tag)} : `
						: ''
				}${title}`}
				subtitle={
					creationDate
						? `${t(
								isCoinvestment === false
									? investExpertiseSynthesisVentureCapitalLang.fund_created_in.tag
									: investExpertiseSynthesisVentureCapitalLang.fund_invested_in.tag,
						  )} ${moment(creationDate).format('Y')}`
						: null
				}
				subtitleBoxComponent="div"
				sx={{ marginTop: 0, color: color && color === 'primary' ? theme.palette.primary.contrastText : '' }}
			/>
			<Grid container spacing={3} alignItems="stretch">
				<Grid item sm={4} md={4} lg={4} xl={4} xs={12}>
					<CSynthesisGraphCapital
						color={color}
						position={position}
						committedCapital={kpis.committedCapital ?? 0}
						calledCapital={kpis.calledCapital ?? 0}
						currency={currency}
					/>
				</Grid>
				<Grid item sm={4} md={4} lg={4} xl={4} xs={12}>
					<CSynthesisGraphTotalValue
						color={color}
						position={position}
						netAssetValue={kpis.netAssetValue ?? 0}
						totalDistributed={kpis.totalDistributed ?? 0}
						distributionsRecallable={kpis.distributionsRecallable ?? 0}
						currency={currency}
					/>
				</Grid>
				<Grid item sm={4} xs={12}>
					<CSynthesisIndicators currency={currency} variant={variant ?? 'outlined'} kpis={kpis} />
				</Grid>
			</Grid>
		</CAppPaper>
	);
};

export default VInvestExpertiseSynthesisVentureCapital;
